import { Command } from 'commander';
import { confirm, password } from '@inquirer/prompts';
import { VaultCryptoService } from '../../services/vault-crypto.service.js';
import { VaultStoreService } from '../../services/vault-store.service.js';
import { createVaultPassphrasePromptConfig } from './shared.js';

interface InitOptions {
    passphrase?: string;
    force?: boolean;
    yes?: boolean;
    json?: boolean;
}

interface InitConfirmationState {
    vaultExists: boolean;
    yes?: boolean;
}

export function shouldRequireInitConfirmation(state: InitConfirmationState): boolean {
    return !state.vaultExists && state.yes !== true;
}

export function formatVaultFirstUseNotice(): string {
    return [
        'Jai1 Vault được mã hóa E2E.',
        '',
        'Trước khi khởi tạo:',
        '- Passphrase dùng để mở vault local. Jai1 không lưu passphrase.',
        '- Recovery key là chìa khóa dự phòng khi đổi máy hoặc quên passphrase.',
        '- Jai1 server chỉ lưu dữ liệu đã mã hóa. Jai1 server không thể khôi phục plaintext.',
        '- Nếu mất passphrase, recovery key và mọi device đã unlock, vault sẽ không thể khôi phục.',
        '- Không lưu recovery key trong repo, chat history, hoặc file plaintext của project.',
    ].join('\n');
}

export function formatVaultBackupGuide(recoveryKey: string): string {
    return [
        'Recovery key (hãy lưu ngoài repo):',
        recoveryKey,
        '',
        'Checklist backup:',
        '- Lưu recovery key trong password manager hoặc nơi offline an toàn.',
        '- Không commit, không paste vào chat, không lưu trong project notes.',
        '- Chạy `j vault list` một lần để kiểm tra passphrase unlock được vault.',
        '- Chạy `j vault sync` để backup encrypted blobs lên Jai1 server.',
        '- Nếu đây là máy cá nhân/tin cậy, chạy `j vault trust` để lần sau không cần nhập lại passphrase.',
        '- Kiểm tra trạng thái trust bằng `j vault trust-status`.',
        '',
        'Khôi phục trên máy mới:',
        '1. Chạy `j auth`.',
        '2. Chạy `j vault sync --pull`.',
        '3. Unlock bằng passphrase, hoặc dùng `j vault list --recovery-key "<recovery-key>"` nếu không còn passphrase.',
    ].join('\n');
}

export function createVaultInitCommand(): Command {
    return new Command('init')
        .description('Initialize an E2E-encrypted Jai1 vault')
        .option('--passphrase <value>', 'Vault passphrase (or JAI1_VAULT_PASSPHRASE)')
        .option('--force', 'Overwrite existing local keyring')
        .option('-y, --yes', 'Confirm first-use warning and initialize vault')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: InitOptions) {
            const store = new VaultStoreService();
            const vaultExists = await store.exists();
            if (vaultExists && !options.force) {
                throw new Error('Vault đã được khởi tạo. Dùng --force nếu muốn ghi đè local keyring.');
            }

            const useJson = !!(options.json || this.optsWithGlobals().json);
            const requiresConfirmation = shouldRequireInitConfirmation({ vaultExists, yes: options.yes });
            if (!vaultExists && !useJson) {
                console.log(formatVaultFirstUseNotice());
                console.log('');
            }
            if (requiresConfirmation) {
                if (!process.stdin.isTTY) {
                    throw new Error('Khởi tạo vault cần xác nhận. Hãy đọc warning recovery rồi chạy lại với --yes.');
                }
                const accepted = await confirm({
                    message: 'Tôi hiểu rằng mất passphrase, recovery key và mọi device đã unlock sẽ không thể khôi phục vault. Khởi tạo vault?',
                    default: false,
                });
                if (!accepted) {
                    console.log('Đã hủy khởi tạo vault.');
                    return;
                }
            }

            const passphrase = options.passphrase || process.env.JAI1_VAULT_PASSPHRASE || (
                process.stdin.isTTY ? await password(createVaultPassphrasePromptConfig('New vault passphrase:')) : undefined
            );
            if (!passphrase) {
                throw new Error('Cần passphrase. Dùng --passphrase hoặc JAI1_VAULT_PASSPHRASE.');
            }

            const result = await new VaultCryptoService().createKeyring(passphrase);
            await store.saveKeyring(result.keyring);
            await store.saveDevice(result.device);

            if (useJson) {
                console.log(JSON.stringify({
                    vaultId: result.keyring.vaultId,
                    recoveryKey: result.recoveryKey,
                    deviceId: result.device.deviceId,
                    deviceFingerprint: result.device.fingerprint,
                    keyringPath: store.getRootDir(),
                }, null, 2));
                return;
            }

            console.log('Đã khởi tạo vault.');
            console.log(`Vault ID: ${result.keyring.vaultId}`);
            console.log(`Device: ${result.device.deviceName} (${result.device.fingerprint})`);
            console.log('');
            console.log(formatVaultBackupGuide(result.recoveryKey));
            console.log('');
            console.log(`Local vault: ${store.getRootDir()}`);
        });
}
